import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Badge,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HubIcon from '@mui/icons-material/Hub';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import RouteIcon from '@mui/icons-material/Route';
import StarIcon from '@mui/icons-material/Star';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CachedIcon from '@mui/icons-material/Cached';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SortIcon from '@mui/icons-material/Sort';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { RootState } from '../../store';
import { selecionarConfiguracao } from '../../store/admin-auth-slice';
import { alternarModoTema } from '../../store/tema-slice';
import {
  useAdminInvalidarCacheConfiguracaoMutation,
  useAdminListarConfiguracoesQuery,
  useAdminMeQuery,
  useAdminObterOnboardingQuery,
} from '../../api';
import { MenuSistemas } from './menu-sistemas';
import { ProfileAdmin } from './profile-admin';
import { OnboardingDialog } from './onboarding-dialog';
import { abrirOnboarding } from '../../lib/onboarding-eventos';
import { inicializarRastreamentoAdmin, rastrearPaginaAdmin } from '../../lib/tracking';
import { podeCriarLoja, rotuloLoja } from '../../lib/lojas-admin';
import { HOST_RETAGUARDA, useUrlSistema } from '../../lib/sistemas-sischef';
import { AvisoLojaExcluida } from './aviso-loja-excluida';
import { BotaoSuporte } from './botao-suporte';

const LARGURA_DRAWER = 260;
const VALOR_CRIAR_NOVA = '__criar_nova__';

/** Passos do onboarding cujo item de menu ganha um "ponto" de pendência. */
type ChaveOnboarding = 'identidade' | 'horarios' | 'pagamento' | 'entrega';

// Item com `caminhoRetaguarda` abre a tela do Sischef Retaguarda em outra aba.
type ItemMenu =
  | { to: string; label: string; icon: JSX.Element; chaveBadge?: ChaveOnboarding }
  | { caminhoRetaguarda: string; label: string; icon: JSX.Element };

const ITENS_MENU: ItemMenu[] = [
  { to: '/admin/configuracao', label: 'Configuração geral', icon: <SettingsIcon />, chaveBadge: 'identidade' },
  { to: '/admin/pedidos', label: 'Pedidos', icon: <ReceiptLongIcon /> },
  // "Acessos ao cardápio" saiu daqui: os pontos de acesso agora se gerenciam na
  // aba do próprio modo, pelo "Gerenciar pontos de acesso" (modal já recortado
  // àquele tipo). A tela continua existindo em /admin/identificadores para links
  // antigos — é a visão da loja inteira, com todos os tipos juntos.
  { to: '/admin/catalogo', label: 'Visibilidade dos itens', icon: <VisibilityIcon /> },
  // A ordem é da Unidade inteira e a tela mora no Gerencial; aqui é só o atalho.
  { caminhoRetaguarda: '/admin/produtos/ordenacaoCardapio.jsf', label: 'Ordem dos itens', icon: <SortIcon /> },
  { to: '/admin/horarios', label: 'Horários de funcionamento', icon: <AccessTimeIcon />, chaveBadge: 'horarios' },
  { to: '/admin/cupons', label: 'Cupons de desconto', icon: <LocalOfferIcon /> },
  // Sempre visível, inclusive para quem não é franquia: nesse caso a tela
  // explica o recurso e oferece pedir a liberação (docs/specs/cupom-rede.md §7).
  { to: '/admin/redes', label: 'Rede de lojas', icon: <HubIcon /> },
  { to: '/admin/formas-pagamento', label: 'Formas de pagamento', icon: <PaymentIcon />, chaveBadge: 'pagamento' },
  { to: '/admin/taxa-entrega', label: 'Taxa de entrega', icon: <RouteIcon />, chaveBadge: 'entrega' },
  { to: '/admin/avaliacoes', label: 'Avaliações', icon: <StarIcon /> },
];

export function AdminLayout(): JSX.Element {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tema = useTheme();
  const ehDesktop = useMediaQuery(tema.breakpoints.up('md'));
  const location = useLocation();
  // Estados separados — evita race entre useMediaQuery e o estado do drawer.
  const [desktopAberto, setDesktopAberto] = useState(true);
  const [mobileAberto, setMobileAberto] = useState(false);
  const idConfig = useSelector((s: RootState) => s.adminAuth.idConfiguracaoSelecionada);
  const mode = useSelector((s: RootState) => s.tema.mode);
  const { data: configs } = useAdminListarConfiguracoesQuery();
  const { data: me } = useAdminMeQuery();
  const { data: onboarding } = useAdminObterOnboardingQuery(idConfig as number, { skip: !idConfig });
  const [invalidarCache, statusCache] = useAdminInvalidarCacheConfiguracaoMutation();

  const mostrarCriarLoja = podeCriarLoja(me, configs);
  const [feedbackCache, setFeedbackCache] = useState<'ok' | 'erro' | null>(null);

  const configAtual = useMemo(() => configs?.find((c) => c.id === idConfig), [configs, idConfig]);

  // A loja escolhida vive no localStorage e sobrevive à troca de login. Se ela
  // não está na lista deste usuário — outra empresa, loja excluída —, tudo o
  // que a tela pede responde 404 e o backoffice fica girando para sempre, com o
  // seletor vazio e sem nada explicando. Reconcilia: cai na primeira loja de
  // quem está logado, ou limpa quando não há nenhuma.
  useEffect(() => {
    if (!configs) return;
    if (configs.length === 0) {
      if (idConfig != null) dispatch(selecionarConfiguracao(null));
      return;
    }
    if (!configs.some((c) => c.id === idConfig)) {
      dispatch(selecionarConfiguracao(configs[0]!.id));
    }
  }, [configs, idConfig, dispatch]);

  // Google Analytics da Parseint no backoffice. Reexecuta quando o `me` chega,
  // para amarrar usuário e unidade à sessão que já começou.
  useEffect(() => {
    inicializarRastreamentoAdmin({ usuarioId: me?.id ?? null, unidadeId: me?.idUnidade ?? null });
  }, [me?.id, me?.idUnidade]);

  useEffect(() => {
    rastrearPaginaAdmin(location.pathname + location.search);
  }, [location.pathname, location.search]);

  async function aoInvalidarCache(): Promise<void> {
    if (!idConfig) return;
    try {
      await invalidarCache(idConfig).unwrap();
      setFeedbackCache('ok');
    } catch {
      setFeedbackCache('erro');
    }
  }

  function alternarDrawer(): void {
    if (ehDesktop) setDesktopAberto((v) => !v);
    else setMobileAberto((v) => !v);
  }

  function fecharSeMobile(): void {
    if (!ehDesktop) setMobileAberto(false);
  }

  function aoTrocarLoja(valor: string): void {
    if (valor === VALOR_CRIAR_NOVA) {
      navigate('/admin/criar-loja');
      fecharSeMobile();
      return;
    }
    dispatch(selecionarConfiguracao(Number(valor) || null));
  }

  const conteudoDrawer: ReactNode = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <MenuSistemas selected="cardapio" />

      <ProfileAdmin />

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Loja em edição
        </Typography>
        <Select
          size="small"
          fullWidth
          value={idConfig ?? ''}
          onChange={(e) => aoTrocarLoja(String(e.target.value))}
          displayEmpty
          sx={{ mt: 0.5 }}
        >
          <MenuItem value="">
            <em>Selecione uma loja</em>
          </MenuItem>
          {configs?.map((c) => (
            <MenuItem key={c.id} value={c.id} sx={!c.ativo ? { color: 'text.disabled' } : undefined}>
              {rotuloLoja(c)}
            </MenuItem>
          ))}
          {mostrarCriarLoja
            ? [
                <Divider key="divisor-criar" sx={{ my: 0.5 }} />,
                <MenuItem key="criar-nova" value={VALOR_CRIAR_NOVA} sx={{ color: 'primary.main' }}>
                  <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                    <AddBusinessIcon fontSize="small" />
                  </ListItemIcon>
                  Criar nova loja
                </MenuItem>,
              ]
            : null}
        </Select>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1 }}>
        {/* "Primeiros passos": abre o dialog de onboarding + % de completude. Some a 100%. */}
        {onboarding && onboarding.percentual < 100 && (
          <ListItemButton
            disabled={!idConfig}
            onClick={() => {
              abrirOnboarding();
              fecharSeMobile();
            }}
          >
            <ListItemIcon>
              <ChecklistIcon />
            </ListItemIcon>
            <ListItemText primary="Primeiros passos" />
            <Chip label={`${onboarding.percentual}%`} size="small" color="warning" />
          </ListItemButton>
        )}
        {ITENS_MENU.map((item) => {
          if ('caminhoRetaguarda' in item) {
            return (
              <ItemMenuRetaguarda
                key={item.caminhoRetaguarda}
                caminho={item.caminhoRetaguarda}
                label={item.label}
                icon={item.icon}
                disabled={!idConfig}
                onClick={fecharSeMobile}
              />
            );
          }
          const chave = item.chaveBadge;
          const pendente = !!onboarding && chave != null && onboarding[chave] === false;
          return (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              selected={location.pathname.startsWith(item.to)}
              disabled={!idConfig}
              onClick={fecharSeMobile}
            >
              <ListItemIcon>
                <Badge color="warning" variant="dot" invisible={!pendente}>
                  {item.icon}
                </Badge>
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>
      {/* Sem "Sair": a sessão do backoffice vem do Sischef pelo `?token=` e não
          existe tela de login aqui. Deslogar só derrubava o token local, deixava
          a sessão do Sischef de pé e prendia o usuário na parede de "acesso deve
          ser feito a partir do Sischef". Quem encerra a sessão é o Sischef. */}

    </Box>
  );

  const drawerVisivelDesktop = ehDesktop && desktopAberto;
  const drawerAtualAberto = ehDesktop ? desktopAberto : mobileAberto;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', md: drawerVisivelDesktop ? `calc(100% - ${LARGURA_DRAWER}px)` : '100%' },
          ml: { xs: 0, md: drawerVisivelDesktop ? `${LARGURA_DRAWER}px` : 0 },
          transition: tema.transitions.create(['margin', 'width'], {
            easing: drawerVisivelDesktop
              ? tema.transitions.easing.easeOut
              : tema.transitions.easing.sharp,
            duration: drawerVisivelDesktop
              ? tema.transitions.duration.enteringScreen
              : tema.transitions.duration.leavingScreen,
          }),
          zIndex: (t) => t.zIndex.drawer - 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={alternarDrawer}
            sx={{ mr: 1 }}
            aria-label={drawerAtualAberto ? 'Fechar menu' : 'Abrir menu'}
          >
            {drawerVisivelDesktop ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {configAtual ? rotuloLoja(configAtual) : 'Sischef Cardápio'}
          </Typography>
          <Tooltip title="Atualizar cardápio (limpar cache)">
            <span>
              <IconButton
                color="inherit"
                onClick={aoInvalidarCache}
                disabled={!idConfig || statusCache.isLoading}
                aria-label="Atualizar cardápio (limpar cache)"
              >
                <CachedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <BotaoSuporte />
          <Tooltip title={mode === 'dark' ? 'Modo claro' : 'Modo escuro'}>
            <IconButton color="inherit" onClick={() => dispatch(alternarModoTema())}>
              {/* Mesmo ícone do Sischef Retaguarda (`brightness_4`), que não troca de
                  glifo entre os modos — quem diz o destino é o tooltip. */}
              <Brightness4Icon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Snackbar
        open={feedbackCache !== null}
        autoHideDuration={3000}
        onClose={() => setFeedbackCache(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={feedbackCache === 'ok' ? 'success' : 'error'}
          variant="filled"
          onClose={() => setFeedbackCache(null)}
        >
          {feedbackCache === 'ok'
            ? 'Cache do cardápio limpo — a loja já reflete as últimas mudanças.'
            : 'Não foi possível limpar o cache. Tente novamente.'}
        </Alert>
      </Snackbar>

      {ehDesktop ? (
        <Drawer
          variant="persistent"
          open={desktopAberto}
          sx={{
            width: LARGURA_DRAWER,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: { width: LARGURA_DRAWER, boxSizing: 'border-box' },
          }}
        >
          {conteudoDrawer}
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileAberto}
          onClose={() => setMobileAberto(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: { width: LARGURA_DRAWER, boxSizing: 'border-box' },
          }}
        >
          {conteudoDrawer}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          mt: 8,
          minWidth: 0,
          ml: { xs: 0, md: drawerVisivelDesktop ? 0 : `-${LARGURA_DRAWER}px` },
          transition: tema.transitions.create('margin', {
            easing: drawerVisivelDesktop
              ? tema.transitions.easing.easeOut
              : tema.transitions.easing.sharp,
            duration: drawerVisivelDesktop
              ? tema.transitions.duration.enteringScreen
              : tema.transitions.duration.leavingScreen,
          }),
        }}
      >
        <AvisoLojaExcluida lojas={configs} />
        {!idConfig ? (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="h6" gutterBottom>
              Selecione uma loja para começar
            </Typography>
            <Typography color="text.secondary">
              {drawerVisivelDesktop
                ? 'Use o menu lateral para escolher qual configuração de cardápio editar.'
                : 'Toque no menu acima para escolher qual configuração de cardápio editar.'}
            </Typography>
          </Box>
        ) : (
          <Outlet />
        )}
      </Box>

      <OnboardingDialog />
    </Box>
  );
}

// Some enquanto o endereço da retaguarda não resolve: item que não leva a lugar nenhum é pior que nenhum.
function ItemMenuRetaguarda({
  caminho,
  label,
  icon,
  disabled,
  onClick,
}: {
  caminho: string;
  label: string;
  icon: JSX.Element;
  disabled: boolean;
  onClick: () => void;
}): JSX.Element | null {
  const url = useUrlSistema(HOST_RETAGUARDA, caminho);
  if (!url) return null;

  return (
    <Tooltip title={disabled ? '' : 'Abre no Sischef Gerencial, em outra aba'} placement="right">
      <ListItemButton
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        disabled={disabled}
        onClick={onClick}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} />
        <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </ListItemButton>
    </Tooltip>
  );
}
